import { useEffect, useState, useMemo } from 'react'
import style from './Feed.module.css'
import FeedCard from './FeedCard'
import FilterIcon from '../../../assets/svgs/Filter'
import useDroppable from '../../../hooks/useDroppable'
import {
  transformCourses,
  transformEvents,
  transformJobs,
  transformNews,
} from '../../../utils/FeedTransformData'

// Simple Search Icon Component
const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={style.searchIcon}
  >
    <path
      d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
      stroke="#6b7280"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 21L16.65 16.65"
      stroke="#6b7280"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const API_URL = process.env.REACT_APP_API_URL || ''

const Feed = () => {
  const filters = ['All', 'Jobs', 'Courses', 'Events', 'News']

  const [selectedFilters, setSelectedFilters] = useState(['All'])
  const [feed, setFeed] = useState([])
  const [openFilter, setOpenFilter] = useState(false)

  // New State for Search & Sort & Pagination
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('date_desc') // 'date_desc', 'date_asc', 'az', 'za'
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      // When search changes fundamentally, reset to Page 1
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1)
      }
    }, 400)
    return () => clearTimeout(handler)
  }, [searchTerm, debouncedSearchTerm])

  const droppableProps = useDroppable(feed, setFeed)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const query = debouncedSearchTerm ? `&search=${encodeURIComponent(debouncedSearchTerm)}` : ''
        const [jobsData, coursesData, eventsData, newsData] = await Promise.all([
          fetch(`${API_URL}/feed/jobs?page=${currentPage}&limit=10${query}`).then((res) => res.json()),
          fetch(`${API_URL}/feed/courses?page=${currentPage}&limit=10${query}`).then((res) => res.json()),
          fetch(`${API_URL}/feed/events?page=${currentPage}&limit=10${query}`).then((res) => res.json()),
          fetch(`${API_URL}/feed/news?page=${currentPage}&limit=10${query}`).then((res) => res.json()),
        ])

        const transformedJobs = transformJobs(jobsData)
        const transformedCourses = transformCourses(coursesData)
        const transformedEvents = transformEvents(eventsData)
        const transformedNews = transformNews(newsData)

        const newFeed = [
          ...transformedJobs,
          ...transformedCourses,
          ...transformedEvents,
          ...transformedNews,
        ]

        if (newFeed.length === 0) {
          setHasMore(false)
        } else {
          setHasMore(true)
        }

        setFeed(newFeed)
      } catch (error) {
        console.error('Error fetching or transforming data:', error)
      }
    }

    fetchData()
  }, [currentPage, debouncedSearchTerm])

  const handleSelect = (filter) => {
    if (filter === 'All') {
      setSelectedFilters(['All'])
    } else {
      let newFilters = selectedFilters.includes('All')
        ? []
        : [...selectedFilters]

      if (newFilters.includes(filter)) {
        newFilters = newFilters.filter((f) => f !== filter)
      } else {
        newFilters.push(filter)
      }

      if (newFilters.length === 0) {
        setSelectedFilters(['All'])
      } else {
        setSelectedFilters(newFilters)
      }
    }
  }

  // --- Filtering Logic ---
  const filteredFeed = useMemo(() => {
    return feed.filter((item) => {
      // 1. Category Filter
      const categoryMatch =
        selectedFilters.includes('All') || selectedFilters.includes(item.category)
      return categoryMatch
    })
  }, [feed, selectedFilters])

  // --- Sorting Logic ---
  const sortedFeed = useMemo(() => {
    const data = [...filteredFeed]

    data.sort((a, b) => {
      switch (sortBy) {
        case 'az':
          return (a.title || '').localeCompare(b.title || '')
        case 'za':
          return (b.title || '').localeCompare(a.title || '')
        case 'date_asc':
          return new Date(a.datePosted) - new Date(b.datePosted)
        case 'date_desc':
        default:
          return new Date(b.datePosted) - new Date(a.datePosted)
      }
    })
    return data
  }, [filteredFeed, sortBy])

  // --- Pagination removed from frontend slice - done via Backend ---



  // --- Date Grouping Helpers (Only used when sorted by Date Descending) ---
  const isToday = (d) => {
    const date = new Date(d)
    const today = new Date()
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  }

  const isYesterday = (d) => {
    const date = new Date(d)
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    return (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    )
  }

  // Render logic to handle grouping vs flat list
  // We only group by date if sortBy is 'date_desc' and we are showing Jobs (or All) 
  // However, for a unified modern feed, strict grouping often complicates mixed content.
  // The user asked for "sort cards based on what i filter".
  // To keep it clean, if the user explicitly sorts by anything other than default, we show a flat list.

  const showGroups = sortBy === 'date_desc' && !searchTerm

  // Helper to render a list of cards
  const renderCardList = (items) => {
    return items.map((feedItem) => {
      // Find original index for drag and drop to work correctly with the main feed state
      const originalIndex = feed.findIndex(i => i.id === feedItem.id);
      // Fallback to index if id not unique/found, though risky for dnd
      const safeIndex = originalIndex >= 0 ? originalIndex : 0;

      return (
        <div
          key={feedItem.id || Math.random()}
          onDrop={(e) => droppableProps.onDrop(e, safeIndex)}
          onDragOver={droppableProps.onDragOver}
        >
          <FeedCard feedItem={feedItem} index={safeIndex} />
        </div>
      )
    })
  }

  return (
    <div className={style.container}>

      {/* Controls Container: Search, Sort, Filter */}
      <div className={style.controlsContainer}>
        {/* Row 1: Search & Sort */}
        <div className={style.searchSortRow}>
          <div className={style.searchWrapper}>
            <SearchIcon />
            <input
              type="text"
              className={style.searchInput}
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className={style.sortWrapper}>
            <select
              className={style.sortSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date_desc">Newest</option>
              <option value="date_asc">Oldest</option>
              <option value="az">A-Z</option>
              <option value="za">Z-A</option>
            </select>
          </div>
        </div>

        {/* Row 2: Filter Chips */}
        <div className={style.filterContainer}>
          {filters.map((filter, index) => (
            <div
              className={`${style.filter} ${selectedFilters.includes(filter) ? style.selected : ''
                }`}
              key={index}
              onClick={() => handleSelect(filter)}
            >
              {filter}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Filters Toggle (Optional - kept for compatibility if needed, but new UI is responsive) */}
      <div onClick={() => setOpenFilter(!openFilter)} className={style.mobileFilters}>
        {openFilter ? '' : <p className={style.filterTitle}>Filter</p>}
        {selectedFilters.length > 0 && !selectedFilters.includes('All') && (
          <div className={style.filterBubble}>{selectedFilters.length}</div>
        )}
        <FilterIcon />
      </div>

      {/* Mobile Filter Panel (Legacy support) */}
      {openFilter && (
        <div className={style.filterContainerMobile}>
          {filters.map((filter, index) => (
            <div
              className={`${style.filter} ${selectedFilters.includes(filter) ? style.selected : ''
                }`}
              key={index}
              onClick={() => handleSelect(filter)}
            >
              {filter}
            </div>
          ))}
        </div>
      )}

      {/* Feed Content */}
      <div className={style.cardsContainer}>
        {sortedFeed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No items found.
          </div>
        ) : (
          // If showing groups (default view), we can optionally add headers. 
          // For now, to satisfy "Modern" and "Sort", a flat sorted list is often cleaner.
          // But if we want to preserve the "Today/Yesterday" headers for the default view:
          showGroups ? (
            <>
              {/* Render Today's items if any */}
              {sortedFeed.some(i => isToday(i.datePosted)) && (
                <div className={style.filteredFeed}>
                  <p className={style.timetext}>Today:</p>
                  {renderCardList(sortedFeed.filter(i => isToday(i.datePosted)))}
                </div>
              )}

              {/* Render Yesterday's items if any */}
              {sortedFeed.some(i => isYesterday(i.datePosted)) && (
                <div className={style.filteredFeed}>
                  <p className={style.timetext}>Yesterday:</p>
                  {renderCardList(sortedFeed.filter(i => isYesterday(i.datePosted)))}
                </div>
              )}

              {/* Render Older items */}
              {sortedFeed.some(i => !isToday(i.datePosted) && !isYesterday(i.datePosted)) && (
                <div className={style.filteredFeed}>
                  <p className={style.timetext}>Earlier:</p>
                  {renderCardList(sortedFeed.filter(i => !isToday(i.datePosted) && !isYesterday(i.datePosted)))}
                </div>
              )}
            </>
          ) : (
            // Flat list for custom sorts or search results means "Today" headers might be confusing order-wise
            <div className={style.filteredFeed}>
              {renderCardList(sortedFeed)}
            </div>
          )
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', margin: '30px 0' }}>
        <button 
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className={style.filterBtn}
          style={{ opacity: currentPage === 1 ? 0.5 : 1, pointerEvents: currentPage === 1 ? 'none' : 'auto' }}
        >
          Previous
        </button>
        <span style={{ fontWeight: '600', color: 'var(--dashbord-headers)', fontSize: '13px' }}>
          Page {currentPage}
        </span>
        <button 
          onClick={() => setCurrentPage(p => p + 1)}
          disabled={!hasMore || sortedFeed.length === 0}
          className={style.filterBtn}
          style={{ opacity: (!hasMore || sortedFeed.length === 0) ? 0.5 : 1, pointerEvents: (!hasMore || sortedFeed.length === 0) ? 'none' : 'auto' }}
        >
          Next
        </button>
      </div>
    </div>
  )
}


export default Feed
